import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { SmartLink, formatBalanceToAbel, toUTCDateTime } from '../../../abelcommon';
import { CenteredTableCell } from '../../../abelcommon/components/CenteredTableCell';
import { useUsername } from '../../../api';
import { ABELSCAN_BLOCK_URL } from '../../../api/abel';
import { loadBalancerAPI } from '../../../api/load-balancer';
import { MountableLoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { useTranslation } from 'react-i18next';

export const LatestAllocateHistorySection = () => {
  const [requestId, setRequestId] = useState<string>(undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { t } = useTranslation();

  const username = useUsername();

  const {
    data: rewardHistory,
    isLoading,
    error,
    isPreviousData,
  } = loadBalancerAPI.hookWrapperQuery.useGetRewardByBlockRanges(
    rowsPerPage,
    page * rowsPerPage,
    username,
    undefined,
    requestId
  );

  const dateFormat = 'yyyy-MM-dd HH:mm:ss';

  const handleChangeRowsPerPage = (nextRowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(nextRowsPerPage);
    setRequestId(new Date().getTime().toString());
  };

  return (
    <Card>
      <CardHeader sx={{ flexWrap: 'wrap' }} title={t('latestAllocateHistorySectionPage.history')} />
      <CardContent sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer sx={{ minHeight: '320px', overflow: 'auto' }}>
          <Table aria-label="simple table" sx={{ minWidth: '500px' }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('latestAllocateHistorySectionPage.blockRange')}</TableCell>
                <TableCell>{t('latestAllocateHistorySectionPage.allocationDate')} (UTC)</TableCell>
                <TableCell>{t('latestAllocateHistorySectionPage.allocatedABEL')}</TableCell>
                <TableCell>{t('latestAllocateHistorySectionPage.share')} (%)</TableCell>
                <TableCell>
                  {t('latestAllocateHistorySectionPage.shareCount')} /{' '}
                  {t('latestAllocateHistorySectionPage.totalCount')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <CenteredTableCell colSpan={7}>
                    <CircularProgress />
                  </CenteredTableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <CenteredTableCell colSpan={7}>
                    <Alert severity="error" sx={{ backgroundColor: 'transparent', p: 0, textAlign: 'center' }}>
                      {t('latestAllocateHistorySectionPage.cannotGetPayoutHistoryInfo')}
                    </Alert>
                  </CenteredTableCell>
                </TableRow>
              ) : rewardHistory.total === 0 ? (
                <TableRow>
                  <CenteredTableCell colSpan={8}>{t('global.noData')}</CenteredTableCell>
                </TableRow>
              ) : (
                rewardHistory.data.map((row, index) => {
                  const allocatedAbel = row.payoutAmount === null ? '--' : formatBalanceToAbel(row.payoutAmount, 7);

                  return (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        <SmartLink to={`${ABELSCAN_BLOCK_URL}/${row.startHeight}`}>{row.startHeight}</SmartLink>
                        {' - '}
                        <SmartLink to={`${ABELSCAN_BLOCK_URL}/${+row.endHeight - 1}`}>{+row.endHeight - 1}</SmartLink>
                      </TableCell>
                      <TableCell>
                        {row.createdAt === null ? '--' : format(toUTCDateTime(row.createdAt), dateFormat)}
                      </TableCell>
                      <TableCell>{allocatedAbel}</TableCell>
                      <TableCell>{((row.shareCount / row.totalCount) * 100).toFixed(2)}</TableCell>
                      <TableCell>{`${row.shareCount} / ${row.totalCount}`}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          sx={{ pl: 0 }}
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={rewardHistory?.total ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, page) => setPage(page)}
          labelRowsPerPage={t('global.rowsPerPage')}
          onRowsPerPageChange={(e) => handleChangeRowsPerPage(+e.target.value)}
        />
        {isPreviousData && <MountableLoadingOverlay />}
      </CardContent>
    </Card>
  );
};
